import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Search, Menu, X, LogIn } from "lucide-react";
import { JOURNAL_LOGO_URL } from "@/lib/constants";
import { useI18n } from "@/lib/i18n";

const NAV_KEYS = [
  { key: "nav.home", to: "/" },
  { key: "nav.current_issue", to: "/current-issue" },
  { key: "nav.articles", to: "/articles" },
  { key: "nav.submit", to: "/submit" },
  { key: "nav.authors", to: "/authors" },
  { key: "nav.editorial_board", to: "/editorial-board" },
  { key: "nav.about", to: "/about" },
  { key: "nav.contact", to: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const [q, setQ] = useState("");
  const location = useLocation();
  const { t, lang, setLang } = useI18n();

  return (
    <header
      data-testid="site-header"
      className="bg-white border-b border-gray-200 sticky top-0 z-50"
    >
      <div className="max-w-[1500px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center shrink-0" data-testid="header-logo-link">
          <img
            src={JOURNAL_LOGO_URL}
            alt="Clinical & Surgical Review"
            className="h-20 md:h-24 lg:h-28 w-auto"
            data-testid="header-logo-img"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6 flex-wrap justify-end" data-testid="primary-nav">
          {NAV_KEYS.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`nav-${it.key.replace("nav.", "")}`}
              className={({ isActive }) =>
                `text-[13px] font-sans uppercase tracking-[0.14em] font-semibold whitespace-nowrap transition-colors pb-1 border-b-2 ${
                  isActive
                    ? "text-[#001F3F] border-[#2BA6A6]"
                    : "text-[#001F3F]/80 border-transparent hover:text-[#2BA6A6]"
                }`
              }
            >
              {t(it.key)}
            </NavLink>
          ))}
          <LangSwitcher lang={lang} setLang={setLang} />
          <Link
            to="/admin/login"
            data-testid="nav-admin-login"
            className="text-[#001F3F]/80 hover:text-[#2BA6A6] transition-colors"
            aria-label={t("nav.admin")}
            title={t("nav.admin")}
          >
            <LogIn className="w-5 h-5" />
          </Link>
          <button
            onClick={() => setSearch((s) => !s)}
            className="text-[#001F3F]/80 hover:text-[#2BA6A6] transition-colors"
            aria-label={t("nav.search")}
            data-testid="header-search-toggle"
          >
            <Search className="w-5 h-5" />
          </button>
        </nav>

        <button
          className="lg:hidden text-[#001F3F]"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {search && (
        <div className="border-t border-gray-100 bg-[#F5F7FA] py-3 px-6" data-testid="header-search-bar">
          <form
            className="max-w-3xl mx-auto flex items-center gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              window.location.href = `/articles?q=${encodeURIComponent(q)}`;
            }}
          >
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("header.search_placeholder")}
              className="flex-1 px-4 py-2 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]"
              data-testid="header-search-input"
            />
            <button
              type="submit"
              className="bg-[#001F3F] text-white px-5 py-2 text-sm font-sans uppercase tracking-wide hover:bg-[#2BA6A6] transition-colors"
              data-testid="header-search-submit"
            >
              {t("header.search_submit")}
            </button>
          </form>
        </div>
      )}

      {open && (
        <div className="lg:hidden border-t border-gray-100 bg-white" data-testid="mobile-nav">
          <div className="px-6 py-3 border-b border-gray-100">
            <LangSwitcher lang={lang} setLang={setLang} />
          </div>
          {NAV_KEYS.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              onClick={() => setOpen(false)}
              className={`block px-6 py-3 text-sm font-sans uppercase tracking-wide border-b border-gray-100 ${
                location.pathname === it.to ? "text-[#2BA6A6]" : "text-[#001F3F]"
              }`}
            >
              {t(it.key)}
            </Link>
          ))}
          <Link
            to="/admin/login"
            onClick={() => setOpen(false)}
            className="block px-6 py-3 text-sm font-sans uppercase tracking-wide text-[#001F3F]"
          >
            {t("nav.admin")}
          </Link>
        </div>
      )}
    </header>
  );
}

function LangSwitcher({ lang, setLang }) {
  return (
    <div className="flex items-center gap-1 text-[12px] font-sans font-semibold tracking-wider" data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setLang("en")}
        className={`px-2 py-0.5 transition-colors ${
          lang === "en" ? "text-[#001F3F] border-b-2 border-[#2BA6A6]" : "text-[#001F3F]/50 hover:text-[#2BA6A6]"
        }`}
        data-testid="lang-en"
      >
        EN
      </button>
      <span className="text-gray-300">|</span>
      <button
        type="button"
        onClick={() => setLang("es")}
        className={`px-2 py-0.5 transition-colors ${
          lang === "es" ? "text-[#001F3F] border-b-2 border-[#2BA6A6]" : "text-[#001F3F]/50 hover:text-[#2BA6A6]"
        }`}
        data-testid="lang-es"
      >
        ES
      </button>
    </div>
  );
}
